/**
 * ActivityCardLearning.jsx  —  NISH FSLP
 * ─────────────────────────────────────────────────────────────────────────────
 * Probes active in all four modes (Story, Name, Find, QA).
 * Five probe types cycle per item: identify → name → attribute → category → function.
 * One probe per item/prompt/question per turn. Same 3-button rating row.
 * Backend: `signals: [{ probe, level }]` added to PATCH /flashcard-sessions/:id/review.
 *
 * Probe targets:
 *   StoryMode    → cycles through scene cards by prompt index (cards[pIdx % cards.length])
 *   NameItMode   → current card being shown
 *   FindPointMode→ current target card
 *   QuestionMode → cycles through scene cards by question index (cards[qIdx % cards.length])
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";

const API = import.meta.env.VITE_API_URL || "http://localhost:5001/api";

const MODES = [
  { id:"story", icon:"📖", label:"Story Telling",  desc:"Child describes the full scene in their own words", color:"#2E86AB", bg:"#EEF6FB" },
  { id:"name",  icon:"🏷️", label:"Name the Items",  desc:"Each item shown one by one — child names it",        color:"#7B2D8B", bg:"#F5EEF8" },
  { id:"find",  icon:"🔍", label:"Find & Point",    desc:"Teacher calls a label — child finds it in the scene",color:"#FF6B35", bg:"#FFF3EE" },
  { id:"qa",    icon:"💬", label:"Question Round",  desc:"Guided questions about the picture — child answers",  color:"#1B8A5A", bg:"#EDF7F2" },
];

const LANGUAGES = [
  { code:"en", name:"English",  voice:"en-IN" },
  { code:"ml", name:"മലയാളം", voice:"ml-IN" },
  { code:"hi", name:"हिंदी",   voice:"hi-IN" },
  { code:"ta", name:"தமிழ்",  voice:"ta-IN" },
  { code:"te", name:"తెలుగు", voice:"te-IN" },
];

// ─── Five probe types ─────────────────────────────────────────────────────────
// Cycle by item index (index % 5). question() receives the card label + category.
// All five work on existing label + category fields — no per-card setup needed.
// Scene-aware: questions reference the pictured scene context naturally.
const PROBES = [
  {
    id: "identify",
    title: "Identify",
    question: (label) => `Can they identify "${label || "this item"}" in the scene?`,
    hint: "Point or tap when named — receptive identification",
  },
  {
    id: "name",
    title: "Name it",
    question: (label) => `Can they name it aloud? (expected: "${label || "the item"}")`,
    hint: "Spontaneous expressive naming — verbal, sign, or AAC",
  },
  {
    id: "attribute",
    title: "Attribute",
    question: () => "Can they describe one attribute of this item?",
    hint: "Color, size, shape, texture, or quantity",
  },
  {
    id: "category",
    title: "Category",
    question: (label) => `What kind of thing is ${label || "this"}?`,
    hint: "Living/non-living, food/tool, animal/object, indoors/outdoors…",
  },
  {
    id: "function",
    title: "Function",
    question: (label) => `What does ${label || "this"} do, or what is it used for?`,
    hint: "Purpose, action, or role in the scene",
  },
];

// ─── Utilities ────────────────────────────────────────────────────────────────
async function apiFetch(method, path, body, headers) {
  const h = { "Content-Type":"application/json", ...(headers||{}) };
  const res = await fetch(`${API}${path}`, { method, headers:h, body: body ? JSON.stringify(body) : undefined });
  const t = await res.text();
  return t ? JSON.parse(t) : {};
}

async function closeSession(sessionId, getAuthHeader, startMs) {
  if (!sessionId) return;
  try {
    await apiFetch("PUT", `/flashcard-sessions/${sessionId}/close`,
      { durationMin: Math.round((Date.now() - (startMs||Date.now())) / 60000) },
      getAuthHeader());
  } catch {}
}

function speak(text, langCode) {
  if (!window.speechSynthesis || !text) return;
  window.speechSynthesis.cancel();
  const lang = LANGUAGES.find(l=>l.code===langCode) || LANGUAGES[0];
  const utt  = new SpeechSynthesisUtterance(text);
  utt.lang   = lang.voice; utt.rate = 0.85; utt.pitch = 1.1;
  window.speechSynthesis.speak(utt);
}

function shuffle(arr) {
  const a = [...arr];
  for (let i=a.length-1; i>0; i--) { const j=Math.floor(Math.random()*(i+1)); [a[i],a[j]]=[a[j],a[i]]; }
  return a;
}

function resolveUrl(url) {
  if (!url) return "";
  if (url.startsWith("data:") || url.startsWith("http")) return url;
  return `${API.replace("/api","")}${url}`;
}

function getLabel(card, language) {
  return card?.labels?.[language] || card?.labels?.en || card?.name || "";
}

function buildQuestions(cards, language) {
  const names = cards.map(c => getLabel(c, language)).filter(Boolean);
  const count = cards.length;
  const pool = [
    { id:"open",     q:"Tell me what you see in this picture.",                         hint:"Open-ended narration" },
    { id:"count-all",q:`How many things can you see? (Answer: ${count})`,              hint:`There are ${count} items.` },
    { id:"name-all", q:"Can you name all the things in this picture?",                  hint:`Items: ${names.join(", ")}` },
    { id:"fav",      q:"Which one is your favourite? Why?",                             hint:"Expression & preference" },
    { id:"color",    q:"What colours can you see in this picture?",                     hint:"Colour awareness" },
    { id:"story",    q:"Can you make up a short story about this picture?",             hint:"Narrative / imagination" },
    ...names.slice(0,4).map((name,i) => ({ id:`point-${i}`, q:`Can you find and point to the "${name}"?`, hint:`Target item: ${name}` })),
    { id:"same-diff",q:"Which two items look similar to each other?",                   hint:"Visual comparison" },
    { id:"category", q:"Can you group any of these items together?",                    hint:"Categorisation" },
    { id:"sequence", q:"If these pictures were a story, what happens first?",           hint:"Sequencing" },
  ];
  const fixed  = pool.slice(0, 3);
  const random = shuffle(pool.slice(3)).slice(0, 3);
  return [...fixed, ...random];
}

// ─── Shared UI ────────────────────────────────────────────────────────────────
function Spinner() {
  return (
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",padding:60,flexDirection:"column",gap:12}}>
      <div style={{width:28,height:28,border:"3px solid #e0e7f0",borderTop:"3px solid #003366",borderRadius:"50%",animation:"spin .7s linear infinite"}}/>
      <div style={{fontSize:13,color:"#aaa"}}>Loading…</div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function SceneGrid({ cards, language, highlightId=null, dimOthers=false, onCardClick=null, showLabels=true, compact=false }) {
  const cols = cards.length<=3?cards.length:cards.length<=6?3:4;
  return (
    <div style={{display:"grid",gridTemplateColumns:`repeat(${cols},1fr)`,gap:compact?8:12,width:"min(640px,96vw)"}}>
      {cards.map((card,i) => {
        const cardId   = card._id||card.id||String(i);
        const isHi     = highlightId===cardId;
        const isDimmed = dimOthers && highlightId && !isHi;
        return (
          <div key={cardId} onClick={()=>onCardClick&&onCardClick(card,i)}
            style={{border:`2.5px solid ${isHi?"#003366":"#e2e8f0"}`,borderRadius:compact?10:14,overflow:"hidden",
                    background:isHi?"#EBF4FB":"white",opacity:isDimmed?0.35:1,
                    boxShadow:isHi?"0 6px 24px rgba(0,51,102,.22)":"0 2px 8px rgba(0,0,0,.06)",
                    cursor:onCardClick?"pointer":"default",transition:"all .25s",display:"flex",flexDirection:"column",alignItems:"center"}}>
            <div style={{width:"100%",height:compact?80:110,display:"flex",alignItems:"center",justifyContent:"center",background:card.color||"#f8f9ff",padding:8}}>
              <img src={resolveUrl(card.imageUrl)} alt={getLabel(card,language)} style={{maxWidth:"100%",maxHeight:"100%",objectFit:"contain"}}/>
            </div>
            {showLabels && (
              <div style={{padding:"5px 8px",fontSize:compact?9:10,fontWeight:700,color:isHi?"#003366":"#444",textAlign:"center",width:"100%",borderTop:"1px solid #f0f4f8",background:isHi?"#EBF4FB":"white"}}>
                {getLabel(card,language)}{card.emoji&&<span style={{marginLeft:4}}>{card.emoji}</span>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ModeBar({ icon, title, subtitle, current, total, color="#003366", onExit }) {
  const pct = total>0?(current/total)*100:0;
  return (
    <>
      <div style={{background:"white",borderBottom:"1px solid #dce3f5",padding:"10px 20px",display:"flex",alignItems:"center",gap:12,flexShrink:0}}>
        <button onClick={onExit} style={{background:"#f0f4ff",border:"none",borderRadius:8,padding:"7px 14px",cursor:"pointer",fontSize:12,color:"#666",fontWeight:700}}>← Exit</button>
        <div style={{flex:1}}>
          <div style={{fontWeight:700,fontSize:13,color}}>{icon} {title}</div>
          {subtitle&&<div style={{fontSize:11,color:"#888"}}>{subtitle}</div>}
        </div>
        {total>0&&<div style={{fontSize:12,color:"#888",fontWeight:600}}>{current} / {total}</div>}
      </div>
      <div style={{height:5,background:"#dce3f5",flexShrink:0}}>
        <div style={{width:`${pct}%`,height:"100%",background:color,transition:"width .4s"}}/>
      </div>
    </>
  );
}

// Quality rating row — unchanged from original
function TeacherRating({ onRate, disabled=false, label="How did the child respond?" }) {
  return (
    <div style={{marginTop:20,width:"min(480px,92vw)"}}>
      <div style={{fontSize:11,fontWeight:700,color:"#888",textAlign:"center",textTransform:"uppercase",letterSpacing:0.8,marginBottom:12}}>{label}</div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
        {[
          {q:1,label:"Needs Help",emoji:"🔴",color:"#C0392B",bg:"#FDEDEC"},
          {q:3,label:"With Prompt",emoji:"🟡",color:"#D4AC0D",bg:"#FEF9E7"},
          {q:5,label:"Got It! ✓",emoji:"🟢",color:"#1B6B45",bg:"#EDF7F2"},
        ].map(r=>(
          <button key={r.q} onClick={()=>onRate(r.q)} disabled={disabled}
            style={{padding:"18px 8px",background:r.bg,border:`2.5px solid ${r.color}`,borderRadius:16,cursor:"pointer",fontWeight:800,fontSize:13,color:r.color,display:"flex",flexDirection:"column",alignItems:"center",gap:6,opacity:disabled?0.5:1,minHeight:72}}>
            <span style={{fontSize:28}}>{r.emoji}</span>{r.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── ProbeRound ───────────────────────────────────────────────────────────────
// Shared by all four modes. One probe per item/prompt/question per turn.
// probe     = PROBES[itemIndex % PROBES.length]
// probeCard = the specific card being probed (shown as thumbnail)
// onRate(level) = called with "absent" | "emerging" | "strong"
function ProbeRound({ probe, probeCard, language, onRate, submitting }) {
  const label    = probeCard ? getLabel(probeCard, language) : "";
  const category = probeCard?.category || "";

  return (
    <div style={{marginTop:16, width:"min(480px,92vw)"}}>
      {/* Probe target thumbnail + question */}
      <div style={{background:"#EBF4FB", borderRadius:12, padding:"13px 16px",
                   border:"1.5px solid #BDD7EE", marginBottom:14,
                   display:"flex", alignItems:"center", gap:14}}>
        {probeCard?.imageUrl && (
          <div style={{width:52, height:52, borderRadius:10, overflow:"hidden",
                       border:"2px solid #BDD7EE", flexShrink:0,
                       background:"#f0f7ff", display:"flex", alignItems:"center", justifyContent:"center"}}>
            <img src={resolveUrl(probeCard.imageUrl)} alt={label}
              style={{maxWidth:"100%", maxHeight:"100%", objectFit:"contain"}}/>
          </div>
        )}
        <div style={{flex:1}}>
          <div style={{display:"flex", alignItems:"center", gap:8, marginBottom:5}}>
            <span style={{fontSize:10, fontWeight:800, color:"#1A4A6E", letterSpacing:.8,
                           textTransform:"uppercase", background:"#BDD7EE",
                           padding:"2px 8px", borderRadius:6}}>
              {probe.title}
            </span>
          </div>
          <div style={{fontWeight:700, fontSize:13, color:"#003366", lineHeight:1.5, marginBottom:3}}>
            {probe.question(label, category)}
          </div>
          <div style={{fontSize:11, color:"#5C7A9E", fontStyle:"italic"}}>
            {probe.hint}
          </div>
        </div>
      </div>
      {/* Rating buttons */}
      <div style={{display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10}}>
        {[
          { level:"absent",   label:"Needs Help",  emoji:"🔴", color:"#C0392B", bg:"#FDEDEC" },
          { level:"emerging", label:"With Prompt", emoji:"🟡", color:"#D4AC0D", bg:"#FEF9E7" },
          { level:"strong",   label:"Got It! ✓",   emoji:"🟢", color:"#1B6B45", bg:"#EDF7F2" },
        ].map(r => (
          <button key={r.level} onClick={() => onRate(r.level)} disabled={submitting}
            style={{padding:"16px 8px", background:r.bg, border:`2.5px solid ${r.color}`,
                     borderRadius:16, cursor:"pointer", fontWeight:800, fontSize:13,
                     color:r.color, display:"flex", flexDirection:"column",
                     alignItems:"center", gap:6, opacity:submitting ? 0.5 : 1, minHeight:70}}>
            <span style={{fontSize:26}}>{r.emoji}</span>
            {r.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function StarBurst({ count=3 }) {
  return (
    <div style={{position:"fixed",inset:0,pointerEvents:"none",zIndex:900,overflow:"hidden"}}>
      {Array.from({length:count*8}).map((_,i)=>{
        const x=Math.random()*100,dur=0.8+Math.random()*0.6,delay=Math.random()*0.4;
        const color=["#FFD700","#FF6B35","#1B6B45","#003366","#7B2D8B","#E65100"][i%6];
        return <div key={i} style={{position:"absolute",left:`${x}%`,top:"60%",width:10,height:10,borderRadius:"50%",background:color,animation:`ac_burst${i%2===0?"Up":"Diag"} ${dur}s ease-out ${delay}s forwards`,opacity:0}}/>;
      })}
      <style>{`@keyframes ac_burstUp{0%{opacity:1;transform:translateY(0) scale(1)}100%{opacity:0;transform:translateY(-300px) scale(.4)}}@keyframes ac_burstDiag{0%{opacity:1;transform:translate(0,0) scale(1)}100%{opacity:0;transform:translate(110px,-260px) scale(.3)}}`}</style>
    </div>
  );
}

function CelebrationScreen({ correct, total, childName, cardTitle, onReplay, onExit }) {
  const pct=total>0?Math.round((correct/total)*100):100;
  const stars=pct>=85?3:pct>=60?2:1;
  const msgs=["Keep practising — you'll get there! 💪","Good effort! 🌟","Great job! Well done 🎉"];
  return (
    <div style={{minHeight:"100vh",background:"linear-gradient(135deg,#003366 0%,#1a3a6e 60%,#2d1b5e 100%)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
      <StarBurst count={stars}/>
      <div style={{background:"white",borderRadius:24,padding:"40px 32px",maxWidth:440,width:"100%",textAlign:"center",boxShadow:"0 24px 80px rgba(0,0,0,.4)",position:"relative",zIndex:10}}>
        <div style={{fontSize:48,marginBottom:8,letterSpacing:4}}>{Array.from({length:3}).map((_,i)=><span key={i} style={{opacity:i<stars?1:0.2}}>⭐</span>)}</div>
        <div style={{fontFamily:"'Playfair Display',serif",fontSize:26,fontWeight:900,color:"#003366",marginBottom:4}}>{stars===3?"Amazing!":stars===2?"Well done!":"Good try!"}</div>
        {childName&&<div style={{fontSize:14,color:"#888",marginBottom:2}}>{childName}</div>}
        {cardTitle&&<div style={{fontSize:11,color:"#aaa",marginBottom:16}}>{cardTitle}</div>}
        <div style={{fontSize:13,color:"#555",marginBottom:28,lineHeight:1.6}}>{msgs[stars-1]}</div>
        <div style={{display:"flex",justifyContent:"center",gap:20,marginBottom:28}}>
          {[{label:"Score",val:`${pct}%`,color:pct>=85?"#1B6B45":pct>=60?"#D4AC0D":"#C0392B"},{label:"Correct",val:correct,color:"#1B6B45"},{label:"Total",val:total,color:"#003366"}].map(s=>(
            <div key={s.label} style={{textAlign:"center"}}>
              <div style={{fontSize:28,fontWeight:900,color:s.color}}>{s.val}</div>
              <div style={{fontSize:10,color:"#aaa",fontWeight:600,textTransform:"uppercase"}}>{s.label}</div>
            </div>
          ))}
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {onReplay&&<button onClick={onReplay} style={{padding:"13px",background:"#003366",color:"white",border:"none",borderRadius:12,fontWeight:700,fontSize:14,cursor:"pointer",boxShadow:"0 4px 16px rgba(0,51,102,.3)"}}>🔄 Play Again</button>}
          <button onClick={onExit} style={{padding:"13px",background:"white",color:"#666",border:"1.5px solid #dce3f5",borderRadius:12,fontWeight:700,fontSize:14,cursor:"pointer"}}>← Back to Setup</button>
        </div>
      </div>
    </div>
  );
}

function SessionNotesScreen({ sessionId, getAuthHeader, startMs, onDone, childName, score }) {
  const [notes,setNotes]=useState(""); const [gen,setGen]=useState(""); const [saving,setSaving]=useState(false);
  const scoreColor=score>=85?"#1B6B45":score>=60?"#D4AC0D":"#C0392B";
  const scoreBg=score>=85?"#EDF7F2":score>=60?"#FEF9E7":"#FDEDEC";
  async function save(){
    setSaving(true);
    try{ await apiFetch("PUT",`/flashcard-sessions/${sessionId}/close`,{durationMin:Math.round((Date.now()-(startMs||Date.now()))/60000),sessionNotes:notes,generalizationNote:gen},getAuthHeader()); }catch{}
    setSaving(false); onDone();
  }
  return (
    <div style={{minHeight:"100vh",background:"linear-gradient(135deg,#003366 0%,#1a3a6e 60%,#2d1b5e 100%)",display:"flex",alignItems:"center",justifyContent:"center",padding:24,fontFamily:"'Segoe UI',Arial,sans-serif"}}>
      <div style={{background:"white",borderRadius:22,padding:"34px 30px",width:500,maxWidth:"96vw",boxShadow:"0 24px 80px rgba(0,0,0,.4)"}}>
        <div style={{textAlign:"center",marginBottom:26}}>
          <div style={{fontSize:40,marginBottom:10}}>📝</div>
          <div style={{fontFamily:"'Playfair Display',serif",fontSize:21,fontWeight:900,color:"#1a1a2e",marginBottom:8}}>Session Notes</div>
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:10}}>
            {childName&&<span style={{fontSize:13,color:"#666",fontWeight:600}}>{childName}</span>}
            {childName&&<span style={{color:"#ddd"}}>·</span>}
            <span style={{fontSize:13,fontWeight:800,color:scoreColor,background:scoreBg,padding:"2px 12px",borderRadius:20,border:`1px solid ${scoreColor}40`}}>{score}% score</span>
          </div>
        </div>
        <div style={{marginBottom:14}}>
          <div style={{fontSize:10,fontWeight:700,color:"#888",textTransform:"uppercase",letterSpacing:.8,marginBottom:6}}>Session Observations</div>
          <textarea autoFocus value={notes} onChange={e=>setNotes(e.target.value)} rows={3} placeholder="Attention span, engagement, behaviour, fatigue, prompts needed…"
            style={{width:"100%",border:"1.5px solid #dce3f5",borderRadius:10,padding:"10px 13px",fontSize:14,outline:"none",resize:"vertical",boxSizing:"border-box",fontFamily:"inherit",lineHeight:1.6}}
            onFocus={e=>e.target.style.borderColor="#003366"} onBlur={e=>e.target.style.borderColor="#dce3f5"}/>
        </div>
        <div style={{marginBottom:26}}>
          <div style={{fontSize:10,fontWeight:700,color:"#888",textTransform:"uppercase",letterSpacing:.8,marginBottom:6}}>Generalization Note <span style={{fontWeight:400,textTransform:"none",letterSpacing:0}}>(optional)</span></div>
          <textarea value={gen} onChange={e=>setGen(e.target.value)} rows={2} placeholder="Did the child apply any learned concepts outside of this session?"
            style={{width:"100%",border:"1.5px solid #dce3f5",borderRadius:10,padding:"10px 13px",fontSize:14,outline:"none",resize:"vertical",boxSizing:"border-box",fontFamily:"inherit",lineHeight:1.6}}
            onFocus={e=>e.target.style.borderColor="#003366"} onBlur={e=>e.target.style.borderColor="#dce3f5"}/>
        </div>
        <div style={{display:"flex",gap:10}}>
          <button onClick={onDone} style={{flex:1,padding:"12px",background:"white",border:"1.5px solid #dce3f5",borderRadius:11,color:"#666",fontWeight:700,fontSize:14,cursor:"pointer"}}>Skip</button>
          <button onClick={save} disabled={saving} style={{flex:2,padding:"12px",background:saving?"#aaa":"#003366",color:"white",border:"none",borderRadius:11,fontWeight:700,fontSize:14,cursor:saving?"default":"pointer",boxShadow:saving?"none":"0 4px 16px rgba(0,51,102,.3)"}}>
            {saving?"Saving…":"✓ Save & Exit"}
          </button>
        </div>
        <div style={{textAlign:"center",marginTop:12,fontSize:11,color:"#aaa"}}>Notes saved to child's session log in Progress Tracker</div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MODE 1 — STORY TELLING  (with probe after each prompt rating)
// ══════════════════════════════════════════════════════════════════════════════
//
// Flow per prompt:
//   1. Prompt shown + full scene grid visible
//   2. Teacher rates quality → moves to 'probe' phase
//   3. Scene grid re-shown with one card highlighted (the probe target)
//      + ProbeRound question shown
//   4. Teacher rates probe → both posted with signals[], advance to next prompt
//
function StoryMode({ cards, language, sessionId, getAuthHeader, childName, cardTitle, onDone }) {
  const m            = MODES[0];
  const sessionStart = useRef(Date.now());
  const closedRef    = useRef(false);
  const submRef      = useRef(false);
  const startMs      = useRef(Date.now());
  const [showNotes,      setShowNotes]      = useState(false);
  const [phase,          setPhase]          = useState("rate"); // "rate" | "probe"
  const [pendingQuality, setPendingQuality] = useState(null);

  const prompts = [
    { id:"p1", text:"Look at this picture. Tell me what you see.",       icon:"👀" },
    { id:"p2", text:"Make up a story about this picture.",                icon:"📖" },
    { id:"p3", text:"Which part of the picture do you like most? Why?",  icon:"❤️" },
  ];

  const [pIdx,    setPIdx]    = useState(0);
  const [history, setHistory] = useState([]);

  const done   = pIdx >= prompts.length;
  const prompt = prompts[pIdx];

  // Probe targets a scene card cycling by prompt index
  const probeCard = cards.length > 0 ? cards[pIdx % cards.length] : null;
  const probe     = PROBES[pIdx % PROBES.length];

  useEffect(() => {
    if (done && !closedRef.current) {
      closedRef.current = true;
      closeSession(sessionId, getAuthHeader, sessionStart.current);
    }
  }, [done]);

  const handleQualityRate = (quality) => {
    if (submRef.current) return;
    setPendingQuality(quality);
    setPhase("probe");
  };

  const handleProbeRate = async (probeLevel) => {
    if (submRef.current || pendingQuality === null) return;
    submRef.current = true;
    try {
      if (sessionId) {
        await apiFetch("PATCH", `/flashcard-sessions/${sessionId}/review`, {
          cardId:     prompts[pIdx].id,
          quality:    pendingQuality,
          responseMs: Date.now() - startMs.current,
          prompted:   pendingQuality < 3,
          signals:    [{ probe: probe.id, level: probeLevel }],
        }, getAuthHeader());
      }
    } catch {}
    submRef.current = false;
    setHistory(h => [...h, { correct: pendingQuality >= 3 }]);
    setTimeout(() => {
      setPIdx(p => p + 1);
      setPhase("rate");
      setPendingQuality(null);
      startMs.current = Date.now();
    }, 300);
  };

  if (done) {
    const correct = history.filter(h=>h.correct).length;
    const score   = prompts.length>0?Math.round((correct/prompts.length)*100):0;
    if (showNotes) return <SessionNotesScreen sessionId={sessionId} getAuthHeader={getAuthHeader} startMs={sessionStart.current} onDone={onDone} childName={childName} score={score}/>;
    return (
      <CelebrationScreen correct={correct} total={prompts.length} childName={childName} cardTitle={cardTitle}
        onReplay={()=>{ closedRef.current=false; sessionStart.current=Date.now(); setPIdx(0); setHistory([]); setPhase("rate"); setPendingQuality(null); setShowNotes(false); }}
        onExit={()=>setShowNotes(true)}/>
    );
  }

  const probeCardId = probeCard?._id || probeCard?.id || String(pIdx % cards.length);

  return (
    <div style={{minHeight:"100vh",background:"#f0f4ff",display:"flex",flexDirection:"column"}}>
      <ModeBar icon={m.icon} title="Story Telling" subtitle={childName} color={m.color} current={pIdx+1} total={prompts.length} onExit={onDone}/>
      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"20px 16px",gap:16}}>

        {phase === "rate" && (
          <>
            {/* Prompt box */}
            <div style={{background:m.bg,border:`2px solid ${m.color}40`,borderRadius:14,padding:"14px 20px",width:"min(600px,96vw)",display:"flex",alignItems:"center",gap:14}}>
              <span style={{fontSize:28,flexShrink:0}}>{prompt.icon}</span>
              <div>
                <div style={{fontSize:10,fontWeight:800,color:m.color,textTransform:"uppercase",letterSpacing:0.8,marginBottom:4}}>Prompt {pIdx+1} of {prompts.length}</div>
                <div style={{fontSize:15,fontWeight:700,color:"#1a2b49",lineHeight:1.4}}>{prompt.text}</div>
              </div>
              <button onClick={()=>speak(prompt.text, language)}
                style={{marginLeft:"auto",background:"white",border:`1.5px solid ${m.color}`,borderRadius:20,padding:"6px 12px",fontSize:11,color:m.color,fontWeight:700,cursor:"pointer",flexShrink:0}}>🔊</button>
            </div>
            {/* Full scene — all cards visible */}
            <SceneGrid cards={cards} language={language} showLabels={true}/>
            <TeacherRating onRate={handleQualityRate} disabled={submRef.current} label="Rate the child's response to this prompt"/>
          </>
        )}

        {phase === "probe" && (
          <>
            {/* Scene with probe target highlighted */}
            <div style={{fontSize:11,color:"#5C7A9E",fontWeight:700,letterSpacing:.5,textTransform:"uppercase",marginBottom:-6}}>
              Now ask about this item ↓
            </div>
            <SceneGrid cards={cards} language={language} showLabels={true}
              highlightId={probeCardId} dimOthers={true}/>
            <ProbeRound
              probe={probe}
              probeCard={probeCard}
              language={language}
              onRate={handleProbeRate}
              submitting={submRef.current}
            />
          </>
        )}

        {/* Progress dots */}
        <div style={{display:"flex",gap:8,marginTop:4}}>
          {prompts.map((_,i)=>(
            <div key={i} style={{width:10,height:10,borderRadius:"50%",background:i<history.length?(history[i]?.correct?"#1B6B45":"#C0392B"):i===pIdx?m.color:"#e2e8f0",transition:"background .3s"}}/>
          ))}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MODE 2 — NAME THE ITEMS  (with probe after each reveal + rating)
// ══════════════════════════════════════════════════════════════════════════════
//
// Flow per card:
//   1. Card shown face-down → tap to reveal label + audio
//   2. Teacher rates quality (phase: "rate") → moves to "probe" phase
//   3. Card stays visible + ProbeRound shown below
//   4. Teacher rates probe → both posted with signals[], advance
//
function NameItMode({ cards, language, sessionId, getAuthHeader, childName, cardTitle, onDone }) {
  const m            = MODES[1];
  const sessionStart = useRef(Date.now());
  const closedRef    = useRef(false);
  const submRef      = useRef(false);
  const startMs      = useRef(Date.now());
  const [showNotes,      setShowNotes]      = useState(false);
  const [phase,          setPhase]          = useState("rate"); // "rate" | "probe"
  const [pendingQuality, setPendingQuality] = useState(null);
  const [index,   setIndex]   = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [history, setHistory] = useState([]);

  const card  = cards[index];
  const label = getLabel(card, language);
  const done  = index >= cards.length;
  const probe = PROBES[index % PROBES.length];

  useEffect(() => {
    if (done && !closedRef.current) {
      closedRef.current = true;
      closeSession(sessionId, getAuthHeader, sessionStart.current);
    }
  }, [done]);

  const revealCard = () => { if (flipped) return; setFlipped(true); speak(label, language); };

  const handleQualityRate = (quality) => {
    if (submRef.current) return;
    setPendingQuality(quality);
    setPhase("probe");
  };

  const handleProbeRate = async (probeLevel) => {
    if (submRef.current || pendingQuality === null) return;
    submRef.current = true;
    try {
      if (sessionId) {
        await apiFetch("PATCH", `/flashcard-sessions/${sessionId}/review`, {
          cardId:     card._id||card.id||String(index),
          quality:    pendingQuality,
          responseMs: Date.now() - startMs.current,
          prompted:   pendingQuality < 3,
          signals:    [{ probe: probe.id, level: probeLevel }],
        }, getAuthHeader());
      }
    } catch {}
    submRef.current = false;
    setHistory(h => [...h, { correct: pendingQuality >= 3 }]);
    setTimeout(() => {
      setIndex(i => i + 1);
      setFlipped(false);
      setPhase("rate");
      setPendingQuality(null);
      startMs.current = Date.now();
    }, 300);
  };

  if (done) {
    const correct = history.filter(h=>h.correct).length;
    const score   = cards.length>0?Math.round((correct/cards.length)*100):0;
    if (showNotes) return <SessionNotesScreen sessionId={sessionId} getAuthHeader={getAuthHeader} startMs={sessionStart.current} onDone={onDone} childName={childName} score={score}/>;
    return (
      <CelebrationScreen correct={correct} total={cards.length} childName={childName} cardTitle={cardTitle}
        onReplay={()=>{ closedRef.current=false; sessionStart.current=Date.now(); setIndex(0); setFlipped(false); setHistory([]); setPhase("rate"); setPendingQuality(null); setShowNotes(false); }}
        onExit={()=>setShowNotes(true)}/>
    );
  }

  return (
    <div style={{minHeight:"100vh",background:"#f0f4ff",display:"flex",flexDirection:"column"}}>
      <ModeBar icon={m.icon} title="Name the Items" subtitle={childName} color={m.color} current={index+1} total={cards.length} onExit={onDone}/>
      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:24}}>

        {/* Card — always visible; tap to reveal when in rate phase */}
        <div onClick={phase === "rate" ? revealCard : undefined}
          style={{width:"min(440px,90vw)",background:"white",borderRadius:24,border:`3px solid ${m.color}`,overflow:"hidden",
                  boxShadow:"0 12px 48px rgba(0,51,102,.18)",
                  cursor:phase==="rate"&&!flipped?"pointer":"default",userSelect:"none"}}>
          <div style={{height:"min(320px,44vw)",display:"flex",alignItems:"center",justifyContent:"center",background:card.color||"#f8f9ff",padding:20}}>
            <img src={resolveUrl(card.imageUrl)} alt={label} style={{maxWidth:"100%",maxHeight:"100%",objectFit:"contain"}}/>
          </div>
          <div style={{padding:"18px 20px",borderTop:"2px solid #e0e7f0",textAlign:"center",minHeight:72,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:8}}>
            {flipped ? (
              <>
                <div style={{fontSize:26,fontWeight:800,color:m.color,lineHeight:1.2}}>{label}</div>
                {card.emoji&&<div style={{fontSize:22}}>{card.emoji}</div>}
                <button onClick={()=>speak(label,language)} style={{background:m.bg,border:"none",borderRadius:20,padding:"5px 14px",fontSize:12,color:m.color,fontWeight:700,cursor:"pointer"}}>🔊 Hear again</button>
              </>
            ) : (
              <div style={{fontSize:15,color:"#aaa",fontWeight:600}}>👆 Tap to reveal</div>
            )}
          </div>
        </div>

        {/* Phase A — quality rating */}
        {flipped && phase === "rate" && (
          <TeacherRating onRate={handleQualityRate} disabled={submRef.current}/>
        )}

        {/* Phase B — probe */}
        {flipped && phase === "probe" && (
          <ProbeRound
            probe={probe}
            probeCard={card}
            language={language}
            onRate={handleProbeRate}
            submitting={submRef.current}
          />
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MODE 3 — FIND & POINT  (with probe slide-up panel after each tap)
// ══════════════════════════════════════════════════════════════════════════════
//
// Flow per target:
//   1. Target label announced + full grid shown — child points, teacher taps
//   2. Outcome shown (correct ✓ / wrong ✗)
//   3. 700ms → probe panel slides up from bottom (target card highlighted)
//   4. Teacher rates probe → review posted with signals[], panel dismisses,
//      advance to next target
//
function FindPointMode({ cards, language, sessionId, getAuthHeader, childName, cardTitle, onDone }) {
  const m            = MODES[2];
  const sessionStart = useRef(Date.now());
  const closedRef    = useRef(false);
  const submRef      = useRef(false);
  const startMs      = useRef(Date.now());
  const pendingReview = useRef(null); // stored on tap, posted after probe
  const [showNotes,     setShowNotes]     = useState(false);
  const [showProbe,     setShowProbe]     = useState(false);
  const [probeSubmitting, setProbeSubmitting] = useState(false);

  const targets  = useRef(shuffle([...cards]));
  const [tIdx,     setTIdx]     = useState(0);
  const [tappedId, setTappedId] = useState(null);
  const [outcome,  setOutcome]  = useState(null);
  const [history,  setHistory]  = useState([]);

  const target = targets.current[tIdx];
  const done   = tIdx >= targets.current.length && !showProbe;
  const probe  = PROBES[tIdx % PROBES.length];

  useEffect(() => {
    if (tIdx >= targets.current.length && !showProbe && !closedRef.current) {
      closedRef.current = true;
      closeSession(sessionId, getAuthHeader, sessionStart.current);
    }
  }, [tIdx, showProbe]);

  useEffect(()=>{
    if (tIdx < targets.current.length && !showProbe) {
      setTimeout(()=>speak(getLabel(targets.current[tIdx], language), language), 300);
    }
  },[tIdx]);

  const handleCardTap = (card) => {
    if (tappedId || submRef.current || showProbe) return;
    const targetId  = target._id||target.id||String(tIdx);
    const tappedCId = card._id||card.id||String(cards.indexOf(card));
    const isCorrect = tappedCId===targetId || getLabel(card,language)===getLabel(target,language);

    setTappedId(tappedCId);
    setOutcome(isCorrect ? "correct" : "wrong");
    speak(isCorrect ? getLabel(target,language) : "Try again", language);

    // Store review data — posted after probe is rated
    pendingReview.current = {
      cardId:     targetId,
      quality:    isCorrect ? 5 : 1,
      responseMs: Date.now() - startMs.current,
      prompted:   false,
    };
    setHistory(h => [...h, { correct: isCorrect }]);

    // After short outcome pause → show probe
    setTimeout(() => setShowProbe(true), 700);
  };

  const handleProbeRate = async (probeLevel) => {
    if (probeSubmitting) return;
    setProbeSubmitting(true);
    try {
      if (sessionId && pendingReview.current) {
        await apiFetch("PATCH", `/flashcard-sessions/${sessionId}/review`, {
          ...pendingReview.current,
          signals: [{ probe: probe.id, level: probeLevel }],
        }, getAuthHeader());
      }
    } catch {}
    pendingReview.current = null;
    setProbeSubmitting(false);
    setShowProbe(false);
    setTimeout(() => {
      setTIdx(i => i + 1);
      setTappedId(null);
      setOutcome(null);
      startMs.current = Date.now();
    }, 200);
  };

  if (done) {
    const correct = history.filter(h=>h.correct).length;
    const score   = targets.current.length>0?Math.round((correct/targets.current.length)*100):0;
    if (showNotes) return <SessionNotesScreen sessionId={sessionId} getAuthHeader={getAuthHeader} startMs={sessionStart.current} onDone={onDone} childName={childName} score={score}/>;
    return (
      <CelebrationScreen correct={correct} total={targets.current.length} childName={childName} cardTitle={cardTitle}
        onReplay={()=>{ closedRef.current=false; sessionStart.current=Date.now(); targets.current=shuffle([...cards]); setTIdx(0); setTappedId(null); setOutcome(null); setHistory([]); setShowProbe(false); setShowNotes(false); }}
        onExit={()=>setShowNotes(true)}/>
    );
  }

  if (tIdx >= targets.current.length) return null; // safety guard while probeCard dismisses

  const targetLabel = getLabel(target, language);
  const targetId    = target._id||target.id||String(tIdx);

  return (
    <div style={{minHeight:"100vh",background:"#f0f4ff",display:"flex",flexDirection:"column"}}>
      <ModeBar icon={m.icon} title="Find & Point" subtitle={childName} color={m.color} current={tIdx+1} total={targets.current.length} onExit={onDone}/>
      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"16px",gap:16}}>

        {/* Target prompt */}
        <div style={{background:"white",border:`2.5px solid ${m.color}`,borderRadius:14,padding:"14px 20px",display:"flex",alignItems:"center",gap:14,width:"min(600px,96vw)",boxShadow:`0 4px 24px ${m.color}22`}}>
          <div style={{fontSize:13,color:"#888",fontWeight:700,flexShrink:0}}>Find:</div>
          <div style={{fontSize:20,fontWeight:900,color:m.color,flex:1}}>{targetLabel}</div>
          <button onClick={()=>speak(targetLabel,language)} style={{background:m.bg,border:`1.5px solid ${m.color}`,borderRadius:20,padding:"6px 14px",fontSize:12,color:m.color,fontWeight:700,cursor:"pointer"}}>🔊 Say it</button>
        </div>

        <div style={{fontSize:12,color:"#8a9ab5",fontWeight:600,opacity:showProbe?0.4:1,transition:"opacity .3s"}}>Teacher: tap the card the child points to</div>

        {/* Grid — dimmed behind probe */}
        <div style={{opacity:showProbe?0.3:1,transition:"opacity .25s",pointerEvents:showProbe?"none":"auto"}}>
          <div style={{display:"grid",gridTemplateColumns:`repeat(${Math.min(cards.length,3)},1fr)`,gap:10,width:"min(640px,96vw)"}}>
            {cards.map((card,i)=>{
              const cardId    = card._id||card.id||String(i);
              const isTapped  = tappedId===cardId;
              const isActualTarget = tappedId && (cardId===targetId || getLabel(card,language)===getLabel(target,language));
              const isWrong   = tappedId&&isTapped&&outcome==="wrong";
              let border="2.5px solid #dce3f5", bg="white";
              if (isActualTarget && tappedId) { border="2.5px solid #1B6B45"; bg="#EDF7F2"; }
              if (isWrong) { border="2.5px solid #C0392B"; bg="#FDEDEC"; }
              return (
                <div key={cardId} onClick={()=>!tappedId&&handleCardTap(card)}
                  style={{border,borderRadius:12,overflow:"hidden",cursor:tappedId?"default":"pointer",background:bg,transition:"all .2s",boxShadow:"0 2px 8px rgba(0,0,0,.06)",display:"flex",flexDirection:"column",alignItems:"center",position:"relative"}}>
                  {tappedId && isActualTarget && <div style={{position:"absolute",top:6,right:6,background:"#1B6B45",color:"white",borderRadius:"50%",width:24,height:24,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:900,zIndex:2}}>✓</div>}
                  {isWrong && <div style={{position:"absolute",top:6,right:6,background:"#C0392B",color:"white",borderRadius:"50%",width:24,height:24,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:900,zIndex:2}}>✗</div>}
                  <div style={{width:"100%",height:100,display:"flex",alignItems:"center",justifyContent:"center",background:card.color||"#f8f9ff",padding:8}}>
                    <img src={resolveUrl(card.imageUrl)} alt={getLabel(card,language)} style={{maxWidth:"100%",maxHeight:"100%",objectFit:"contain"}}/>
                  </div>
                  <div style={{padding:"5px 8px",fontSize:9,fontWeight:700,color:"#444",textAlign:"center",borderTop:"1px solid #f0f4f8",width:"100%"}}>
                    {getLabel(card,language)}{card.emoji&&<span style={{marginLeft:3}}>{card.emoji}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {outcome && !showProbe && (
          <div style={{fontSize:17,fontWeight:800,color:outcome==="correct"?"#1B6B45":"#C0392B",animation:"fadeUp .25s ease"}}>
            {outcome==="correct"?`✅ Correct! "${targetLabel}" found!`:`❌ Not quite — let's try again`}
          </div>
        )}
        {!tappedId && <div style={{fontSize:11,color:"#aaa",fontWeight:600,textAlign:"center"}}>👆 After the child points to a card, tap it here to record their answer</div>}
      </div>

      {/* Probe panel — slides up from bottom after each tap */}
      {showProbe && (
        <div style={{position:"fixed",bottom:0,left:0,right:0,
                     background:"white",borderTop:`2.5px solid ${m.color}`,
                     borderRadius:"20px 20px 0 0",padding:"20px 24px 32px",
                     boxShadow:"0 -8px 40px rgba(0,0,0,.18)",
                     display:"flex",flexDirection:"column",alignItems:"center",
                     animation:"slideUp .28s cubic-bezier(.22,.68,0,1.2) both",
                     zIndex:50}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4,
                       width:"min(480px,92vw)"}}>
            <div style={{fontSize:9,fontWeight:800,color:m.color,textTransform:"uppercase",letterSpacing:.8}}>
              {outcome==="correct"?"✅ Found it!":"❌ Not quite"} — now check this skill:
            </div>
          </div>
          <ProbeRound
            probe={probe}
            probeCard={target}
            language={language}
            onRate={handleProbeRate}
            submitting={probeSubmitting}
          />
        </div>
      )}

      <style>{`
        @keyframes fadeUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes slideUp{from{transform:translateY(100%);opacity:0}to{transform:translateY(0);opacity:1}}
      `}</style>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MODE 4 — QUESTION ROUND  (with probe after each question rating)
// ══════════════════════════════════════════════════════════════════════════════
//
// Flow per question:
//   1. Question shown + compact scene grid + hint toggle
//   2. Teacher rates quality (phase: "rate") → moves to "probe" phase
//   3. Scene grid re-shown with probe target highlighted + ProbeRound
//   4. Teacher rates probe → both posted with signals[], advance
//
function QuestionMode({ cards, language, sessionId, getAuthHeader, childName, cardTitle, onDone }) {
  const m            = MODES[3];
  const sessionStart = useRef(Date.now());
  const closedRef    = useRef(false);
  const submRef      = useRef(false);
  const startMs      = useRef(Date.now());
  const [showNotes,      setShowNotes]      = useState(false);
  const [phase,          setPhase]          = useState("rate"); // "rate" | "probe"
  const [pendingQuality, setPendingQuality] = useState(null);

  const questions = useRef(buildQuestions(cards, language));
  const [qIdx,    setQIdx]    = useState(0);
  const [history, setHistory] = useState([]);
  const [showHint,setShowHint]= useState(false);

  const done = qIdx >= questions.current.length;
  const q    = questions.current[qIdx];

  // Probe targets a scene card cycling by question index
  const probeCard = cards.length > 0 ? cards[qIdx % cards.length] : null;
  const probe     = PROBES[qIdx % PROBES.length];

  useEffect(() => {
    if (done && !closedRef.current) {
      closedRef.current = true;
      closeSession(sessionId, getAuthHeader, sessionStart.current);
    }
  }, [done]);

  useEffect(()=>{
    if (!done && q && phase === "rate") { setShowHint(false); setTimeout(()=>speak(q.q, language), 300); }
  },[qIdx, phase]);

  const handleQualityRate = (quality) => {
    if (submRef.current) return;
    setPendingQuality(quality);
    setPhase("probe");
  };

  const handleProbeRate = async (probeLevel) => {
    if (submRef.current || pendingQuality === null) return;
    submRef.current = true;
    try {
      if (sessionId) {
        await apiFetch("PATCH", `/flashcard-sessions/${sessionId}/review`, {
          cardId:     q.id,
          quality:    pendingQuality,
          responseMs: Date.now() - startMs.current,
          prompted:   pendingQuality < 3,
          signals:    [{ probe: probe.id, level: probeLevel }],
        }, getAuthHeader());
      }
    } catch {}
    submRef.current = false;
    setHistory(h => [...h, { correct: pendingQuality >= 3 }]);
    setTimeout(() => {
      setQIdx(i => i + 1);
      setPhase("rate");
      setPendingQuality(null);
      startMs.current = Date.now();
    }, 300);
  };

  if (done) {
    const correct = history.filter(h=>h.correct).length;
    const score   = questions.current.length>0?Math.round((correct/questions.current.length)*100):0;
    if (showNotes) return <SessionNotesScreen sessionId={sessionId} getAuthHeader={getAuthHeader} startMs={sessionStart.current} onDone={onDone} childName={childName} score={score}/>;
    return (
      <CelebrationScreen correct={correct} total={questions.current.length} childName={childName} cardTitle={cardTitle}
        onReplay={()=>{ closedRef.current=false; sessionStart.current=Date.now(); questions.current=buildQuestions(cards,language); setQIdx(0); setHistory([]); setShowHint(false); setPhase("rate"); setPendingQuality(null); setShowNotes(false); }}
        onExit={()=>setShowNotes(true)}/>
    );
  }

  const probeCardId = probeCard?._id || probeCard?.id || String(qIdx % cards.length);

  return (
    <div style={{minHeight:"100vh",background:"#f0f4ff",display:"flex",flexDirection:"column"}}>
      <ModeBar icon={m.icon} title="Question Round" subtitle={childName} color={m.color} current={qIdx+1} total={questions.current.length} onExit={onDone}/>
      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"20px 16px",gap:16}}>

        {phase === "rate" && (
          <>
            {/* Question box */}
            <div style={{background:"white",border:`2.5px solid ${m.color}`,borderRadius:16,padding:"20px 24px",width:"min(600px,96vw)",boxShadow:`0 4px 32px ${m.color}22`}}>
              <div style={{fontSize:10,fontWeight:800,color:m.color,textTransform:"uppercase",letterSpacing:0.8,marginBottom:10}}>{m.icon} Question {qIdx+1}</div>
              <div style={{fontSize:18,fontWeight:800,color:"#1a2b49",lineHeight:1.5,marginBottom:12}}>{q.q}</div>
              <div style={{display:"flex",gap:10}}>
                <button onClick={()=>speak(q.q,language)} style={{background:m.bg,border:`1.5px solid ${m.color}`,borderRadius:20,padding:"6px 14px",fontSize:11,color:m.color,fontWeight:700,cursor:"pointer"}}>🔊 Read aloud</button>
                <button onClick={()=>setShowHint(h=>!h)} style={{background:"#f8f9ff",border:"1.5px solid #dce3f5",borderRadius:20,padding:"6px 14px",fontSize:11,color:"#8a9ab5",fontWeight:700,cursor:"pointer"}}>💡 {showHint?"Hide hint":"Show hint"}</button>
              </div>
              {showHint && <div style={{marginTop:10,padding:"8px 12px",background:m.bg,borderRadius:8,fontSize:11,color:m.color,fontWeight:600}}>Hint: {q.hint}</div>}
            </div>
            {/* Compact scene overview */}
            <SceneGrid cards={cards} language={language} compact={true} showLabels={true}/>
            <TeacherRating onRate={handleQualityRate} disabled={submRef.current} label="Rate the child's answer"/>
          </>
        )}

        {phase === "probe" && (
          <>
            {/* Scene with probe target highlighted */}
            <div style={{fontSize:11,color:"#5C7A9E",fontWeight:700,letterSpacing:.5,textTransform:"uppercase",marginBottom:-6}}>
              Now ask about this item ↓
            </div>
            <SceneGrid cards={cards} language={language} compact={true} showLabels={true}
              highlightId={probeCardId} dimOthers={true}/>
            <ProbeRound
              probe={probe}
              probeCard={probeCard}
              language={language}
              onRate={handleProbeRate}
              submitting={submRef.current}
            />
          </>
        )}

        {/* Progress dots */}
        <div style={{display:"flex",gap:8,marginTop:4}}>
          {questions.current.map((_,i)=>(
            <div key={i} style={{width:10,height:10,borderRadius:"50%",background:i<history.length?(history[i]?.correct?"#1B6B45":"#C0392B"):i===qIdx?m.color:"#e2e8f0",transition:"background .3s"}}/>
          ))}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SETUP SCREEN  (unchanged from original)
// ══════════════════════════════════════════════════════════════════════════════
function SetupScreen({ getAuthHeader, onStart, linkedId }) {
  const { user } = useAuth();
  const [children, setChildren] = useState([]);
  const [acList,   setAcList]   = useState([]);
  const [childId,  setChildId]  = useState("");
  const [acId,     setAcId]     = useState("");
  const [language, setLanguage] = useState("en");
  const [mode,     setMode]     = useState("story");
  const [loading,  setLoading]  = useState(true);
  const [starting, setStarting] = useState(false);
  const [search,   setSearch]   = useState("");
  const [err,      setErr]      = useState("");

  useEffect(()=>{
    (async()=>{
      try {
        const [cd,ad] = await Promise.all([
          apiFetch("GET","/children?limit=200",    null,getAuthHeader()),
          apiFetch("GET","/activitycard?limit=100",null,getAuthHeader()),
        ]);
        const kids = Array.isArray(cd?.children)?cd.children:Array.isArray(cd)?cd:[];
        setChildren(kids);
        setAcList(ad?.activityCards||[]);
        if (kids.length===1) setChildId(kids[0]._id);
        if (linkedId) setAcId(linkedId);
      } catch(e){ setErr(e.message); }
      setLoading(false);
    })();
  },[]);

  const filteredAc    = acList.filter(ac=>!search||ac.title.toLowerCase().includes(search.toLowerCase()));
  const selectedAc    = acList.find(a=>a._id===acId);
  const selectedChild = children.find(c=>c._id===childId);
  const selectedMode  = MODES.find(m=>m.id===mode);
  const canStart      = childId && acId;

  const start = async () => {
    if (!canStart) return;
    setStarting(true); setErr("");
    try {
      const data = await apiFetch("GET",`/activitycard/${acId}`,null,getAuthHeader());
      if (!data.success) throw new Error(data.message||"Failed to load activity card");
      const full = data.activityCard;
      apiFetch("POST",`/activitycard/${acId}/use`,null,getAuthHeader()).catch(()=>{});
      const sd = await apiFetch("POST","/flashcard-sessions/log",{
        childId, moduleType:"activitycard", moduleRef:acId, setTitle:full.title,
        sessionNotes:`Mode: ${mode}`, durationMin:0,
        conductorRole: user?.role==="parent"?"parent":"teacher",
        sessionType:   user?.role==="parent"?"parent-led":"teacher-led",
      }, getAuthHeader());
      const sessionId = sd.session?._id || sd._id || null;
      onStart({ child:selectedChild, activityCard:full, cards:full.cards||[], language, mode, sessionId });
    } catch(e){ setErr(e.message); }
    setStarting(false);
  };

  if (loading) return <div style={{background:"#f0f4ff",minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center"}}><Spinner/></div>;

  return (
    <div style={{background:"#f0f4ff",minHeight:"100vh",padding:"40px 16px",fontFamily:"'Segoe UI',Arial,sans-serif"}}>
      <div style={{maxWidth:540,margin:"0 auto"}}>
        <div style={{marginBottom:28,textAlign:"center"}}>
          <div style={{fontSize:32,marginBottom:8}}>🖼️</div>
          <div style={{fontFamily:"'Playfair Display',serif",fontSize:26,fontWeight:900,color:"#003366",marginBottom:4}}>Activity Card Session</div>
          <div style={{fontSize:13,color:"#888"}}>Guided learning from composite picture cards · one skill probe per item</div>
        </div>

        {err&&<div style={{background:"#FDEDEC",color:"#C0392B",padding:"10px 14px",borderRadius:8,fontSize:12,marginBottom:16,fontWeight:600}}>{err}</div>}

        {/* Child */}
        <div style={{background:"white",borderRadius:14,padding:18,marginBottom:14,border:"1.5px solid #e0e7f0",boxShadow:"0 1px 4px rgba(0,0,0,.05)"}}>
          <label style={{display:"block",fontSize:10,fontWeight:700,color:"#888",textTransform:"uppercase",letterSpacing:0.8,marginBottom:8}}>Child *</label>
          {children.length===0
            ? <div style={{fontSize:12,color:"#aaa",textAlign:"center",padding:14}}>No children found</div>
            : <div style={{display:"flex",flexDirection:"column",gap:7,maxHeight:190,overflowY:"auto"}}>
                {children.map(c=>{
                  const age=c.dob?new Date().getFullYear()-new Date(c.dob).getFullYear():null;
                  const sel=childId===c._id;
                  return (
                    <div key={c._id} onClick={()=>setChildId(c._id)}
                      style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",border:`2px solid ${sel?"#003366":"#dce3f5"}`,borderRadius:9,background:sel?"#EBF4FB":"white",cursor:"pointer",transition:"all .15s"}}>
                      <div style={{width:30,height:30,borderRadius:"50%",background:"#003366",display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontSize:11,fontWeight:800,flexShrink:0}}>{(c.name||"?")[0].toUpperCase()}</div>
                      <div style={{flex:1}}>
                        <div style={{fontWeight:700,fontSize:13,color:"#003366"}}>{c.name}</div>
                        {age&&<div style={{fontSize:10,color:"#888"}}>{age} years old</div>}
                      </div>
                      {sel&&<span style={{color:"#003366",fontWeight:900,fontSize:16}}>✓</span>}
                    </div>
                  );
                })}
              </div>
          }
        </div>

        {/* Activity Card */}
        <div style={{background:"white",borderRadius:14,padding:18,marginBottom:14,border:"1.5px solid #e0e7f0",boxShadow:"0 1px 4px rgba(0,0,0,.05)"}}>
          <label style={{display:"block",fontSize:10,fontWeight:700,color:"#888",textTransform:"uppercase",letterSpacing:0.8,marginBottom:8}}>Activity Card *</label>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search activity cards…"
            style={{width:"100%",border:"1.5px solid #dce3f5",borderRadius:8,padding:"7px 11px",fontSize:12,outline:"none",marginBottom:10,boxSizing:"border-box"}}/>
          {filteredAc.length===0
            ? <div style={{fontSize:12,color:"#aaa",textAlign:"center",padding:20}}>{acList.length===0?"No activity cards found. Create one in the Activity Card module.":"No activity cards match your search."}</div>
            : <div style={{display:"flex",flexDirection:"column",gap:8,maxHeight:220,overflowY:"auto"}}>
                {filteredAc.map(ac=>{
                  const sel=acId===ac._id;
                  return (
                    <div key={ac._id} onClick={()=>setAcId(ac._id)}
                      style={{padding:"10px 14px",display:"flex",alignItems:"center",gap:12,border:`2px solid ${sel?"#003366":"#dce3f5"}`,borderRadius:9,background:sel?"#EBF4FB":"white",cursor:"pointer",transition:"all .15s"}}>
                      <span style={{fontSize:20,flexShrink:0}}>🖼️</span>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontWeight:700,fontSize:13,color:"#003366",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{ac.title}</div>
                        <div style={{fontSize:10,color:"#888",marginTop:1}}>{ac.subtitle||"Activity Card"}{ac.usedCount?" · Used "+ac.usedCount+"×":""}</div>
                      </div>
                      {sel&&<span style={{color:"#003366",fontWeight:900,fontSize:16}}>✓</span>}
                    </div>
                  );
                })}
              </div>
          }
        </div>

        {/* Language + mode selects */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
          <div style={{background:"white",borderRadius:14,padding:16,border:"1.5px solid #e0e7f0"}}>
            <label style={{display:"block",fontSize:10,fontWeight:700,color:"#888",textTransform:"uppercase",letterSpacing:0.8,marginBottom:8}}>Language</label>
            <select value={language} onChange={e=>setLanguage(e.target.value)} style={{width:"100%",padding:"9px 10px",border:"1.5px solid #dce3f5",borderRadius:8,fontSize:12,outline:"none",background:"white"}}>
              {LANGUAGES.map(l=><option key={l.code} value={l.code}>{l.name}</option>)}
            </select>
          </div>
          <div style={{background:"white",borderRadius:14,padding:16,border:"1.5px solid #e0e7f0"}}>
            <label style={{display:"block",fontSize:10,fontWeight:700,color:"#888",textTransform:"uppercase",letterSpacing:0.8,marginBottom:8}}>Activity Mode</label>
            <select value={mode} onChange={e=>setMode(e.target.value)} style={{width:"100%",padding:"9px 10px",border:"1.5px solid #dce3f5",borderRadius:8,fontSize:12,outline:"none",background:"white"}}>
              {MODES.map(m=><option key={m.id} value={m.id}>{m.icon} {m.label}</option>)}
            </select>
          </div>
        </div>

        {/* Mode picker grid */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:10,marginBottom:20}}>
          {MODES.map(m=>{
            const isActive=mode===m.id;
            return (
              <div key={m.id} onClick={()=>setMode(m.id)}
                style={{padding:"14px 12px",background:isActive?m.bg:"white",border:`2px solid ${isActive?m.color:"#dce3f5"}`,borderRadius:12,cursor:"pointer",textAlign:"center",transition:"all .15s",boxShadow:isActive?"0 4px 16px rgba(0,0,0,.08)":"none"}}>
                <div style={{fontSize:22,marginBottom:4}}>{m.icon}</div>
                <div style={{fontSize:11,fontWeight:800,color:isActive?m.color:"#555",marginBottom:2}}>{m.label}</div>
                <div style={{fontSize:9,color:"#aaa",lineHeight:1.4}}>{m.desc}</div>
                {isActive&&<div style={{marginTop:6,fontSize:10,color:m.color,fontWeight:800}}>✓ Selected</div>}
              </div>
            );
          })}
        </div>

        {selectedAc && selectedMode && (
          <div style={{background:"#EBF4FB",borderRadius:10,padding:"10px 14px",marginBottom:16,fontSize:12,color:"#1A4A6E",fontWeight:600,display:"flex",gap:8,alignItems:"center"}}>
            <span style={{fontSize:16}}>ℹ️</span>
            <span>{selectedMode.label} · {selectedMode.desc} · Each item includes one skill probe</span>
          </div>
        )}

        <button onClick={start} disabled={!canStart||starting}
          style={{width:"100%",padding:"15px",background:canStart?"#003366":"#ccc",color:"white",border:"none",borderRadius:12,fontWeight:800,fontSize:15,cursor:canStart?"pointer":"not-allowed",boxShadow:canStart?"0 6px 24px rgba(0,51,102,.3)":"none"}}>
          {starting?"Loading…":`▶ Start ${selectedMode?.label||"Activity"}`}
        </button>
        <div style={{textAlign:"center",fontSize:11,color:"#aaa",marginTop:12}}>Child responses tracked per question · one skill probe per item</div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ROOT EXPORT  (unchanged)
// ══════════════════════════════════════════════════════════════════════════════
export default function ActivityCardLearning() {
  const { getAuthHeader } = useAuth();
  const { state: navState } = useLocation();
  const [phase,    setPhase]    = useState("setup");
  const [activity, setActivity] = useState(null);

  const launch = (data) => { setActivity(data); setPhase("play"); };
  const exit   = async () => {
    if (navState?.workplanId && navState?.entryId) {
      try {
        await fetch(`${API}/workplan/${navState.workplanId}/entry/${navState.entryId}`, {
          method:"PATCH", headers:{"Content-Type":"application/json",...getAuthHeader()},
          body: JSON.stringify({status:"done"}),
        });
      } catch {}
    }
    setActivity(null); setPhase("setup");
  };

  if (phase==="setup") return <SetupScreen getAuthHeader={getAuthHeader} onStart={launch} linkedId={navState?.linkedId}/>;

  if (phase==="play" && activity) {
    const { child, activityCard, cards, language, mode, sessionId } = activity;
    const childName = child?.name||"";
    const cardTitle = activityCard?.title||"";
    const common = { cards, language, sessionId, getAuthHeader, childName, cardTitle, onDone:exit };

    switch (mode) {
      case "story": return <StoryMode     {...common}/>;
      case "name":  return <NameItMode    {...common}/>;
      case "find":  return <FindPointMode {...common}/>;
      case "qa":    return <QuestionMode  {...common}/>;
      default:
        return (
          <div style={{padding:40,textAlign:"center",color:"#8a9ab5",fontFamily:"'Segoe UI',Arial,sans-serif"}}>
            <div style={{fontSize:32,marginBottom:12}}>⚠️</div>
            Unknown mode: <strong>{mode}</strong><br/>
            <button onClick={exit} style={{marginTop:20,padding:"10px 24px",background:"#003366",color:"white",border:"none",borderRadius:8,cursor:"pointer",fontWeight:700}}>← Back</button>
          </div>
        );
    }
  }

  return null;
}